import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Label, RichTextEditor } from "@tellescope/ui";
import type { ImageUploadHandler } from "@tellescope/ui";
import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import type { RichTextBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import type { BlockTypeEditorProps } from "../types";
import { useAuth } from "@tellescope/tanstack-query";

type RichTextBlockEditorProps = BlockTypeEditorProps<RichTextBlock>;

export function RichTextBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: RichTextBlockEditorProps) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { uploadFileAsync } = useUploadTellescopeFile({
    session,
    returnDownloadURL: true,
  });

  const uploadImage: ImageUploadHandler = useCallback(
    async (file: File) => {
      if (!session) {
        console.error("No active session for image upload");
        return null;
      }

      try {
        const result = await uploadFileAsync(
          {
            name: file.name,
            size: file.size,
            type: file.type,
            publicRead: true,
          },
          file
        );

        if (result.downloadURL) {
          return { url: result.downloadURL };
        }

        return null;
      } catch (error) {
        console.error("Failed to upload image:", error);
        return null;
      }
    },
    [session, uploadFileAsync]
  );

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Rich Text Content")}</Label>
        <RichTextEditor
          content={block.props.content}
          onChange={(content) => updateProps({ content })}
          placeholder={t("Enter formatted content...")}
          onImageUpload={uploadImage}
        />
      </div>
      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
